/**
 * FilterEditor Component
 *
 * Provides a UI for editing individual filter configurations.
 * Allows users to select field, operator, and value for filtering linkedTable data.
 * Uses combobox to show all possible values for the selected field.
 *
 * Uses field UUIDs internally for referential integrity, but displays field names in UI.
 *
 * @module FilterEditor
 */

import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import type { LinkedTableFilterApi, FieldDataType } from '@loader/shared-types';
import { FILTER_OPERATORS_BY_TYPE } from '@loader/shared-types';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@loader/ui';
import type { LinkedTableVariableApi } from '@loader/shared-types';

import type { FilterEditorProps } from './types';
import { FieldSelector } from './FieldSelector';
import { OperatorSelector } from './OperatorSelector';
import { FilterValueSelector } from './FilterValueSelector';

/** Check if a filter has a non-empty static value */
function hasFilterValue(filter: LinkedTableFilterApi): boolean {
  if (filter.value === null || filter.value === undefined) return false;
  if (Array.isArray(filter.value)) return filter.value.length > 0;
  return String(filter.value) !== '';
}

/** Sentinel value for "no variable selected" in the variable dropdown */
const NO_VARIABLE_VALUE = '_none_';

/** Shared variable select dropdown used in filter variable binding */
function VariableSelect({
  value,
  variables,
  canEdit,
  onValueChange,
}: {
  value: string;
  variables: LinkedTableVariableApi[];
  canEdit: boolean;
  onValueChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onValueChange} disabled={!canEdit}>
      <SelectTrigger className="bg-background w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_VARIABLE_VALUE}>None</SelectItem>
        {variables.map((v) => (
          <SelectItem key={v.id} value={v.name}>
            ${v.name} ({v.type})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * FilterEditor component for configuring linkedTable filters
 *
 * Provides dropdowns for field and operator selection, and type-specific value inputs.
 * Supports all filter operators defined in LinkedTableFilter type.
 */
export const FilterEditor: React.FC<FilterEditorProps> = ({
  canEdit = true,
  filter,
  availableFields,
  linkedTableId,
  versionSelections,
  isIncomplete = false,
  variables = [],
  onUpdate,
  onRemove,
}) => {
  // Debounce timer ref for value updates
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 300; // ms

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Debounced version of onUpdate for value changes
   * Prevents excessive API calls during rapid value selection
   */
  const debouncedOnUpdate = useCallback(
    (updatedFilter: LinkedTableFilterApi) => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Schedule the update
      debounceTimerRef.current = setTimeout(() => {
        onUpdate(updatedFilter);
      }, DEBOUNCE_DELAY);
    },
    [onUpdate]
  );

  /**
   * Get selected field details
   */
  const selectedField = useMemo(() => {
    return availableFields.find((f) => f.id === filter.field_id);
  }, [availableFields, filter.field_id]);

  /**
   * Handle field selection change
   * Resets operator and value when field changes to prevent invalid configurations
   */
  const handleFieldChange = (field_id: string) => {
    const field = availableFields.find((f) => f.id === field_id);
    const fieldType = field?.data_type || 'text';
    const allowedOperators = FILTER_OPERATORS_BY_TYPE[fieldType as FieldDataType] || FILTER_OPERATORS_BY_TYPE.text;
    // Keep current operator if still valid for new field type, otherwise reset to equals
    const newOperator = allowedOperators.includes(filter.operator) ? filter.operator : 'equals';
    onUpdate({
      ...filter,
      field_id,
      field_name: field?.name || undefined,
      table: field?.table_alias || filter.table, // Update table to match field's table
      operator: newOperator,
      value: '', // Reset value when field changes
    });
  };

  /**
   * Handle operator selection change
   * Clears value when switching to is_null/is_not_null (no value needed)
   */
  const handleOperatorChange = (operator: string) => {
    const isNullOperator = operator === 'is_null' || operator === 'is_not_null';
    onUpdate({
      ...filter,
      operator: operator as LinkedTableFilterApi['operator'],
      // Clear value for null operators, keep existing value otherwise
      value: isNullOperator ? null : filter.value,
      // Ensure field_name is set if we have a field selected
      field_name: selectedField?.name || filter.field_name,
    });
  };

  return (
    <div
      className={`relative space-y-2 rounded-md border bg-background p-3 ${
        isIncomplete
          ? 'border-destructive bg-destructive/5 border-2'
          : 'border-border'
      }`}
    >
      {/* Remove button - positioned top right, centered on border - Hidden for viewers */}
      {canEdit && (
        <Button
          variant="outline"
          size="icon"
          className="absolute -top-3 -right-3 h-6 w-6 rounded-full"
          onClick={() => onRemove(filter)}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      <div className="space-y-2">
        {/* Field selector */}
        <FieldSelector
          canEdit={canEdit}
          filter={filter}
          availableFields={availableFields}
          selectedField={selectedField}
          onFieldChange={handleFieldChange}
        />

        {/* Operator selector */}
        <OperatorSelector
          canEdit={canEdit}
          filter={filter}
          selectedField={selectedField}
          onOperatorChange={handleOperatorChange}
        />

        {/* Value input - hidden when a variable is bound */}
        {!filter.variable_name && (
          <FilterValueSelector
            canEdit={canEdit}
            filter={filter}
            selectedField={selectedField}
            linkedTableId={linkedTableId}
            versionSelections={versionSelections}
            onUpdate={onUpdate}
            debouncedOnUpdate={debouncedOnUpdate}
          />
        )}

        {/* Variable binding - mutual exclusion with free value */}
        {variables.length > 0 && filter.operator !== 'is_null' && filter.operator !== 'is_not_null' && (
          <>
            {/* No value and no variable — show "or" divider + variable select */}
            {!filter.variable_name && !hasFilterValue(filter) && (
              <div>
                <div className="relative py-2">
                  <hr className="border-border" />
                  <span className="text-muted-foreground bg-background absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 text-xs">or</span>
                </div>
                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">Variable</label>
                  <VariableSelect
                    value={NO_VARIABLE_VALUE}
                    variables={variables}
                    canEdit={canEdit}
                    onValueChange={(value) => {
                      onUpdate({
                        ...filter,
                        variable_name: value === NO_VARIABLE_VALUE ? undefined : value,
                        value: '',
                      });
                    }}
                  />
                </div>
              </div>
            )}

            {/* Value is set — show link to switch to variable */}
            {!filter.variable_name && hasFilterValue(filter) && (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs underline"
                onClick={() => {
                  onUpdate({ ...filter, variable_name: variables[0].name, value: '' });
                }}
                disabled={!canEdit}
              >
                Use variable instead
              </button>
            )}

            {/* Variable is bound — show variable select + link to switch back */}
            {filter.variable_name && (
              <div className="space-y-1">
                <VariableSelect
                  value={filter.variable_name}
                  variables={variables}
                  canEdit={canEdit}
                  onValueChange={(value) => {
                    onUpdate({
                      ...filter,
                      variable_name: value === NO_VARIABLE_VALUE ? undefined : value,
                    });
                  }}
                />
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground text-xs underline"
                  onClick={() => onUpdate({ ...filter, variable_name: undefined })}
                  disabled={!canEdit}
                >
                  Use static value
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
